import Input from "components/Input";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { useRef, useState } from "react";
import Button from "components/Button";
import settingsService, {
  IConfigureIntegrationRequest,
} from "api/settingsService";
import { ENotificationType, notification } from "utils/notification";
import HollowButton from "components/HollowButton";
import { useAuth } from "react-oidc-context";
import { IntegrationType } from "dto/Settings/IntegrationType";
import { IntegrationProvider } from "dto/Settings/IntegrationProvider";
import { useTranslation } from "react-i18next";

// Metoda uwierzytelnienia w KSeF. „token" — token aplikacyjny wygenerowany w
// aplikacji KSeF; „certificate" — certyfikat KSeF (PEM) + klucz prywatny (PEM).
type AuthMethod = "token" | "certificate";

// Wczytany plik PEM: nazwa do prezentacji + treść do wysłania na BE.
interface PemFile {
  name: string;
  content: string;
}

// Pliki PEM to tekst — wczytujemy zawartość bezpośrednio (bez uploadu do S3).
const isPem = (content: string) => content.includes("-----BEGIN");

// Klucz prywatny zaszyfrowany hasłem (PKCS#8) — wtedy wymagane jest hasło.
const isEncryptedKey = (content: string) =>
  content.includes("ENCRYPTED PRIVATE KEY");

// Atrybuty zniechęcające przeglądarkę i menedżery haseł do auto-uzupełniania
// pól (NIP/hasło potrafią być wypełniane zapamiętanymi wartościami).
const noAutofillProps = (name: string, autoComplete = "off") => ({
  autoComplete,
  name,
  "data-lpignore": "true",
  "data-form-type": "other",
});

const ConfigureKSeF: FC<Props> = ({ onSuccess, toggle }) => {
  const { t } = useTranslation();
  const auth = useAuth();

  const { mutate: integration, isLoading } =
    settingsService.useConfigureIntegration();

  // Integracja KSeF odbywa się wyłącznie przez certyfikat (uwierzytelnienie XAdES);
  // metoda jest stała, dlatego przełącznik metody nie jest renderowany.
  const [method] = useState<AuthMethod>("certificate");

  const [data, setData] = useState({
    nip: "",
    token: "",
    label: auth.user?.profile.name ?? "",
  });

  const [certificate, setCertificate] = useState<PemFile | null>(null);
  const [privateKey, setPrivateKey] = useState<PemFile | null>(null);
  const [keyPassword, setKeyPassword] = useState("");
  const certificateInput = useRef<HTMLInputElement | null>(null);
  const privateKeyInput = useRef<HTMLInputElement | null>(null);

  const [error, setError] = useState<string>(null);

  const readPemFile = (
    event: React.ChangeEvent<HTMLInputElement>,
    setter: (file: PemFile | null) => void,
  ) => {
    const file = event.target.files?.[0];
    // Pozwala ponownie wgrać ten sam plik (reset value inputu).
    event.target.value = "";
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const content = String(reader.result ?? "");
      if (!isPem(content)) {
        setError(t("integrations.kSeFInvalidPem"));
        setter(null);
        return;
      }
      setError(null);
      setter({ name: file.name, content });
    };
    reader.readAsText(file);
  };

  // Klucz wymaga hasła, gdy jest zaszyfrowany (ENCRYPTED PRIVATE KEY).
  const keyNeedsPassword = !!privateKey && isEncryptedKey(privateKey.content);

  // Czy formularz jest gotowy do wysłania dla wybranej metody.
  const isComplete =
    !!data.nip &&
    (method === "token"
      ? !!data.token
      : !!certificate?.content &&
        !!privateKey?.content &&
        (!keyNeedsPassword || !!keyPassword));

  const handleClick = () => {
    const payload: IConfigureIntegrationRequest = {
      Type: IntegrationType.KSeF,
      Provider: IntegrationProvider.KSeF,
      Nip: data.nip,
      ...(method === "token"
        ? { Token: data.token }
        : {
            PublicKey: certificate?.content,
            PrivateKey: privateKey?.content,
            PrivateKeyPassword: keyPassword || undefined,
          }),
    };

    setError(null);
    integration(payload, {
      onSuccess: () => {
        onSuccess(data.nip);
        toggle();
      },
      onError: (response: any) => {
        const name = response.response.data.errorCodes[0].message;

        notification({
          type: ENotificationType.ERROR,
          title: t("integrations.createIntegrationFailed"),
          text: name,
        });
        setError(name);
      },
    });
  };

  const renderFileRow = (
    label: string,
    file: PemFile | null,
    inputRef: React.MutableRefObject<HTMLInputElement | null>,
    onPick: (e: React.ChangeEvent<HTMLInputElement>) => void,
    onRemove: () => void,
  ) => (
    <div className={styles.fileRow}>
      <span className={styles.fileLabel}>{label}</span>
      {file ? (
        <div className={styles.fileChip}>
          <span className={styles.fileName} title={file.name}>
            {file.name}
          </span>
          <button
            type="button"
            className={styles.fileRemove}
            onClick={onRemove}
            title={t("common.delete")}
          >
            ✕
          </button>
        </div>
      ) : (
        <button
          type="button"
          className={styles.fileButton}
          onClick={() => inputRef.current?.click()}
        >
          {t("integrations.kSeFUploadFile")}
        </button>
      )}
      <input
        type="file"
        accept=".pem,.crt,.cer,.key"
        ref={inputRef}
        style={{ display: "none" }}
        onChange={onPick}
      />
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>{t("integrations.configureKSeFTitle")}</div>

      {/* Wybór metody nie jest renderowany — integracja KSeF jest wyłącznie
          certyfikatowa (patrz inicjalizacja `method`). */}

      <div className={styles.body}>
        {method === "token"
          ? t("integrations.kSeFDescription")
          : t("integrations.kSeFCertificateDescription")}
      </div>

      <div className={styles.form}>
        <Input
          value={data.nip}
          onChange={(t) => setData({ ...data, nip: t })}
          label={t("forms.nip")}
          autoComplete="off"
          inputProps={noAutofillProps("ksef-nip-field")}
        />

        {method === "token" ? (
          <Input
            value={data.token}
            type="password"
            onChange={(t) => setData({ ...data, token: t })}
            label={t("integrations.token")}
          />
        ) : (
          <>
            <Input
              value={keyPassword}
              type="password"
              onChange={setKeyPassword}
              label={
                keyNeedsPassword
                  ? t("integrations.kSeFKeyPasswordRequired")
                  : t("integrations.kSeFKeyPassword")
              }
              autoComplete="new-password"
              inputProps={noAutofillProps("ksef-keypass-field", "new-password")}
            />
            {renderFileRow(
              t("integrations.kSeFCertificate"),
              certificate,
              certificateInput,
              (e) => readPemFile(e, setCertificate),
              () => setCertificate(null),
            )}
            {renderFileRow(
              t("integrations.kSeFPrivateKey"),
              privateKey,
              privateKeyInput,
              (e) => readPemFile(e, setPrivateKey),
              () => setPrivateKey(null),
            )}
          </>
        )}

        {error && <div className={styles.error}>{error}</div>}
        <div className={styles.buttonsWrapper}>
          <HollowButton text={t("common.cancel")} onClick={() => toggle()} />
          <Button
            text={t("common.save")}
            onClick={handleClick}
            loading={isLoading}
            disabled={isLoading || !isComplete}
          />
        </div>
      </div>
    </div>
  );
};

export default ConfigureKSeF;
